"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { Toast, type ToastTone } from "./Toast";
import s from "./ToastProvider.module.css";

export interface AvisoToast {
  title: string;
  message?: string;
  tone?: ToastTone;
  /** Milisegundos hasta cerrarse solo. `0` lo deja hasta que lo cierren. */
  duracion?: number;
}

interface Contexto {
  /** Muestra un aviso y devuelve su id, por si hay que cerrarlo antes. */
  mostrar: (aviso: AvisoToast) => number;
  cerrar: (id: number) => void;
}

const ToastContext = createContext<Contexto | null>(null);

interface AvisoVivo extends AvisoToast {
  id: number;
}

const DURACION_DEFAULT = 4000;

/** Va una vez, arriba de todo (en `app/layout.tsx`). Los avisos se muestran con `useToast()`. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<AvisoVivo[]>([]);
  const siguiente = useRef(1);

  const cerrar = useCallback((id: number) => {
    setAvisos((lista) => lista.filter((a) => a.id !== id));
  }, []);
  const mostrar = useCallback((aviso: AvisoToast) => {
    const id = siguiente.current++;
    setAvisos((lista) => [...lista, { ...aviso, id }]);
    return id;
  }, []);
  const valor = useMemo(() => ({ mostrar, cerrar }), [mostrar, cerrar]);

  return (
    <ToastContext.Provider value={valor}>
      {children}
      <div className={s.region} aria-live="polite" aria-relevant="additions">
        {avisos.map((aviso) => (
          <AvisoConTiempo key={aviso.id} aviso={aviso} onCerrar={() => cerrar(aviso.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function AvisoConTiempo({ aviso, onCerrar }: { aviso: AvisoVivo; onCerrar: () => void }) {
  const [pausado, setPausado] = useState(false);
  const duracion = aviso.duracion ?? DURACION_DEFAULT;

  // El reloj arranca de nuevo cada vez que el puntero se va: es más simple
  // que descontar lo que faltaba, y el usuario lo lee igual.
  useEffect(() => {
    if (pausado || duracion === 0) return;
    const t = setTimeout(onCerrar, duracion);
    return () => clearTimeout(t);
  }, [pausado, duracion, onCerrar]);

  return (
    <div
      className={s.item}
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onFocus={() => setPausado(true)}
      onBlur={() => setPausado(false)}
    >
      <Toast title={aviso.title} message={aviso.message} tone={aviso.tone} onDismiss={onCerrar} />
    </div>
  );
}

export function useToast(): Contexto {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast() necesita un <ToastProvider> más arriba en el árbol.");
  }
  return ctx;
}
